import { Box, HStack, Text, Wrap, WrapItem } from '@chakra-ui/react';
import SettingsEthernetSharpIcon from '@gamma/ui/icons/SettingsEthernetSharpIcon';
import { memo, ReactNode } from 'react';
import { LocationTag } from '@investigator/shared/components';
import { i18n } from '@investigator/shared/localization';
import { MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';
import { DetailsGridRow } from '@investigator/features/detections/components';
import { EntityMetaData } from '../../../types';
import { SkeletonLoader } from '../../SkeletonLoader';
import { useEntitiesContext } from '../../../context/EntitiesContext';

export interface LogscaleModalSectionProps {
  showHeader?: boolean;
  displayCard?: boolean;
}

const { networkInformation, origin, asn, clientProtocols, serverProtocols } =
  i18n.pages.entityDetails;

export const LogscaleModalSection = memo(
  ({ showHeader = true, displayCard = false }: LogscaleModalSectionProps) => {
    const { selectedEntity } = useEntitiesContext();
    const {
      entityCategory,
      logscaleMetaData,
      logscaleRespData,
      logscaleOrigData,
      isSimpleSearchMetaLoading,
      isSimpleSearchOrigLoading,
      isSimpleSearchRespLoading,
    } = selectedEntity || {};

    // Select correct geo fields based on entity category
    const isSourceEntity = entityCategory === 'source';
    const metaData: EntityMetaData = isSourceEntity
      ? {
          countryCode: logscaleMetaData?.['orig_cc'],
          country: logscaleMetaData?.['orig_geo.country'],
          city: logscaleMetaData?.['orig_geo.city'],
          asnValue: logscaleMetaData?.['id.orig_h.asn'],
          asnOrg: logscaleMetaData?.['id.orig_h.org'],
        }
      : {
          countryCode: logscaleMetaData?.['resp_cc'],
          country: logscaleMetaData?.['resp_geo.country'],
          city: logscaleMetaData?.['resp_geo.city'],
          asnValue: logscaleMetaData?.['id.resp_h.asn'],
          asnOrg: logscaleMetaData?.['id.resp_h.org'],
        };

    // Get client protocols from originator data
    const { service: clientProtocolsService } = logscaleOrigData || {};

    // Get server protocols from responder data
    const { service: serverProtocolsService } = logscaleRespData || {};

    const asnData = [metaData.asnValue, metaData.asnOrg].filter((data) => data);
    const detailsAlignment = displayCard ? 'column' : 'row';

    const progressiveLoading = (loading: boolean, node?: ReactNode) => {
      return loading ? (
        <Box width="130px">
          <SkeletonLoader rowCount={1} />
        </Box>
      ) : (
        node || MISSING_DATA_PLACEHOLDER
      );
    };

    return (
      <>
        {showHeader && (
          <HStack marginBottom={2} data-testid="networkinfo-header">
            <SettingsEthernetSharpIcon />
            <Text textStyle="h5">{networkInformation}</Text>
          </HStack>
        )}
        <Wrap spacing={2} maxWidth="400px" margin={1}>
          <WrapItem display="flex" flexDirection={detailsAlignment}>
            <DetailsGridRow
              title={<Text minW="130">{origin}</Text>}
              data-testid="networkinfo-origin"
            >
              {progressiveLoading(
                !!isSimpleSearchMetaLoading,
                metaData.countryCode && (
                  <Box minW="130">
                    <LocationTag
                      countryCode={metaData.countryCode}
                      country={metaData.country}
                      city={metaData.city}
                    />
                  </Box>
                ),
              )}
            </DetailsGridRow>
          </WrapItem>
          <WrapItem display="flex" flexDirection={detailsAlignment}>
            <DetailsGridRow
              title={<Text minW="130">{asn}</Text>}
              data-testid="networkinfo-asn"
            >
              <Text minW="130" maxW="160">
                {progressiveLoading(
                  !!isSimpleSearchMetaLoading,
                  asnData?.join(', '),
                )}
              </Text>
            </DetailsGridRow>
          </WrapItem>
          <WrapItem display="flex" flexDirection={detailsAlignment}>
            <DetailsGridRow
              title={<Text minW="130">{clientProtocols}</Text>}
              data-testid="networkinfo-client-protocols"
            >
              <Text textTransform="uppercase" minW="130">
                {progressiveLoading(
                  !!isSimpleSearchOrigLoading,
                  clientProtocolsService,
                )}
              </Text>
            </DetailsGridRow>
          </WrapItem>
          <WrapItem display="flex" flexDirection={detailsAlignment}>
            <DetailsGridRow
              title={<Text minW="130">{serverProtocols}</Text>}
              data-testid="networkinfo-server-protocols"
            >
              <Text textTransform="uppercase" minW="130">
                {progressiveLoading(
                  !!isSimpleSearchRespLoading,
                  serverProtocolsService,
                )}
              </Text>
            </DetailsGridRow>
          </WrapItem>
        </Wrap>
      </>
    );
  },
);
